/**
 * Traduction des erreurs d'accès micro brutes (DOMException / Error) en messages
 * humains et orientés action.
 *
 * Pourquoi un matching sur la CHAÎNE du message plutôt que sur `err.name` :
 * la lib VAD (@ricky0123/vad-web) re-emballe parfois la DOMException d'origine
 * dans un `new Error(String(err))`, ce qui écrase `err.name` ("Error"). Le texte
 * du message conserve en revanche le code d'origine ("NotFoundError: …"). C'est
 * déjà le pattern retenu dans src/hooks/useVoiceRecording.ts.
 *
 * Garde-fou produit : on NE dit JAMAIS « branche un micro ». Quasiment tous les
 * ordinateurs ont un micro intégré ; le vrai blocage est l'autorisation côté
 * navigateur ou côté système. Les messages rassurent puis orientent.
 */

export type MicIssueKind = "blocked" | "no-device" | "other";

export type MicIssue = {
  kind: MicIssueKind;
  title: string;
  description: string;
};

/**
 * Classe un message d'erreur micro brut en MicIssue lisible.
 *
 * - "blocked"   : l'accès a été explicitement refusé pour le site (permission
 *                 navigateur). Action la plus directe : débloquer dans le navigateur.
 * - "no-device" : aucun périphérique actif détecté (souvent micro coupé au niveau
 *                 du système, pas une absence de matériel) ou message inconnu.
 *                 On oriente vers navigateur ET système.
 */
export function classifyMicError(rawMessage: string | null | undefined): MicIssue {
  const msg = (rawMessage ?? "").toLowerCase();

  const isBlocked =
    msg.includes("notallowed") ||
    msg.includes("permission") ||
    msg.includes("denied") ||
    msg.includes("security");

  if (isBlocked) {
    return {
      kind: "blocked",
      title: "Le micro est bloqué",
      description:
        "L'accès au micro a été refusé pour ce site. Clique sur le 🔒 à gauche de l'adresse, autorise le micro, puis recharge la page.",
    };
  }

  return {
    kind: "no-device",
    title: "Juliette ne t'entend pas",
    description:
      "Pas besoin d'un micro spécial : presque tous les ordinateurs en ont un intégré. Il faut juste l'autoriser — regarde du côté de ton navigateur (le 🔒 à gauche de l'adresse) ou des réglages de ton système (confidentialité → micro).",
  };
}
